// src/components/ProcedureShow.tsx
import { Box, Divider, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import {
  FunctionField,
  Show,
  SimpleShowLayout,
  useLocaleState,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "react-admin";

import {
  NoteKind,
  RemovePlannedProcedureActivitySessionDocument,
  RemovePlannedProcedureActivitySessionMutation,
  RemovePlannedProcedureActivitySessionMutationVariables,
} from "../generated/graphql";
import { client } from "../lib/apolloClient";
import { formatDateTimeInline } from "../lib/dateUtils";
import { formatName } from "../lib/formatName";
import { canRemoveProcedureActivitySession } from "../lib/procedureSession";
import { borderRadius, color, space, typography } from "../theme/designTokens";
import { ProcedureRecord } from "../types/procedure";
import { CustomConfirm } from "./CustomConfirm";
import { DocumentationSyncStatus } from "./fields/DocumentationSyncStatus";
import { ProcedureAllPatientsField } from "./fields/ProcedureAllPatientsField";
import { ProcedureStatusChip } from "./fields/ProcedureStatusChip";
import { SourceSystemChip } from "./fields/SourceSystemChip";
import { InfoField } from "./InfoField";
import { ProcedureActivitySessionCard } from "./ProcedureActivitySessionCard";

const NOTE_KIND_LABEL_KEYS: Record<NoteKind, string> = {
  [NoteKind.ActivityDocumentation]: "Procedure.fields.activityDocumentation",
  [NoteKind.AiSummary]: "Procedure.fields.aiSummary",
  [NoteKind.AiNote]: "Procedure.fields.aiNote",
  [NoteKind.Note]: "Procedure.fields.manualNote",
};

type RemoveSessionState = {
  procedureId: string;
  sessionId: string;
  title: string;
};

const ProcedureTitle = () => {
  const record = useRecordContext<ProcedureRecord>();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Typography variant="body1">
      {record.organization?.name || translate("Procedure.organization")} - {translate("Procedure.title")} -{" "}
      {record.codeDisplay || record.externalId}
    </Typography>
  );
};

const ProcedureShowFields = ({
  record,
  onRemoveClick,
}: {
  record: ProcedureRecord;
  onRemoveClick: (session: RemoveSessionState) => void;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const activitySessions = record.activitySessions?.nodes ?? [];

  return (
    <Box>
      <Box sx={{ display: "flex", gap: space.xs, mb: space.xs }}>
        <ProcedureStatusChip size="medium" />
        <SourceSystemChip translatePrefix="Procedure.fields.sourceSystem" />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: space.xs }}>
        <InfoField label={translate("Procedure.fields.externalId")} value={record.externalId} />
        <InfoField label={translate("Procedure.fields.codeDisplay")} value={record.codeDisplay} />
        <InfoField label={translate("Procedure.fields.description")} value={record.description} />
      </Box>

      <Divider sx={{ my: space.xs }} />
      <Typography variant="h6" gutterBottom>
        {translate("Procedure.titles.timing")}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: space.xs }}>
        <InfoField
          label={translate("Procedure.fields.plannedStart")}
          value={formatDateTimeInline(record.plannedStart, locale)}
        />
        <InfoField
          label={translate("Procedure.fields.plannedEnd")}
          value={formatDateTimeInline(record.plannedEnd, locale)}
        />
        <InfoField
          label={translate("Procedure.fields.performedStart")}
          value={formatDateTimeInline(record.performedStart, locale)}
        />
        <InfoField
          label={translate("Procedure.fields.performedEnd")}
          value={formatDateTimeInline(record.performedEnd, locale)}
        />
        <Box>
          <InfoField
            label={translate("Procedure.fields.acknowledgedAt")}
            value={formatDateTimeInline(record.acknowledgedAt, locale)}
          />
          <DocumentationSyncStatus syncs={record.documentationSyncsList} />
        </Box>
      </Box>

      <Divider sx={{ my: space.xs }} />
      <Typography variant="h6" gutterBottom>
        {translate("Procedure.titles.participants")}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: space.xs }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {translate("Procedure.fields.patients")}
          </Typography>
          <ProcedureAllPatientsField />
        </Box>
        <InfoField label={translate("Procedure.fields.performer")} value={formatName(record.performer?.user)} />
      </Box>

      <Divider sx={{ my: space.xs }} />
      <Typography variant="h6" gutterBottom>
        {translate("Procedure.titles.notes")}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: space.xs }}>
        <InfoField label={translate("Procedure.fields.notes")} value={record.notes} />
        {record.documentation && (
          <Box>
            <Typography variant="caption" color={color.gray}>
              {translate("Procedure.fields.documentation")}
            </Typography>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ whiteSpace: "pre-wrap" }}>
              {record.documentation}
            </Typography>
          </Box>
        )}
        {record.procedureNotesList?.map(np => {
          const note = np.procedureNotes;
          if (!note) return null;
          const label = translate(NOTE_KIND_LABEL_KEYS[note.kind] ?? "Procedure.fields.manualNote");
          const residents = note.seniorList.map(senior => senior.name).join(", ");
          return (
            <Box key={note.id}>
              <Typography variant="caption" color={color.gray}>
                {residents ? `${residents} · ${label}` : label}
              </Typography>
              <Typography variant="subtitle1" fontWeight="medium" sx={{ whiteSpace: "pre-wrap" }}>
                {note.note}
              </Typography>
              <DocumentationSyncStatus syncs={note.documentationSyncsList} />
            </Box>
          );
        })}
      </Box>

      {record.activitySessions && (
        <>
          <Divider sx={{ my: space.xs }} />
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 0.75 }}>
            <Typography variant="h6">{translate("Procedure.titles.activitySessions")}</Typography>
            <Box
              sx={{
                backgroundColor: color.primary,
                color: color.white,
                fontSize: typography.fontSize.xxs,
                fontWeight: typography.fontWeight.semibold,
                minWidth: 24,
                height: 24,
                px: 1,
                borderRadius: borderRadius.lg,
                display: "inline-grid",
                placeItems: "center",
              }}
            >
              {activitySessions.length}
            </Box>
          </Box>
          <Typography variant="body2" color={color.gray} sx={{ mb: 2.25 }}>
            {translate("Procedure.session.subtitle")}
          </Typography>
          {activitySessions.length > 0 ? (
            activitySessions.map((session, index) => (
              <ProcedureActivitySessionCard
                key={session.id}
                procedureId={record.id}
                session={session}
                index={index}
                canRemove={canRemoveProcedureActivitySession(record.status, session.sessionStatus)}
                onRemove={onRemoveClick}
              />
            ))
          ) : (
            <Box
              sx={{
                border: `1px dashed ${color.panelBorder}`,
                borderRadius: borderRadius.sm,
                backgroundColor: color.background2,
                p: 3,
                textAlign: "center",
              }}
              data-testid="procedure-sessions-empty"
            >
              <Typography sx={{ fontWeight: typography.fontWeight.semibold }}>
                {translate("Procedure.session.emptyTitle")}
              </Typography>
              <Typography variant="body2" color={color.gray} sx={{ mt: 0.5 }}>
                {translate("Procedure.session.emptySubtitle")}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export const ProcedureShow = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [removeState, setRemoveState] = useState<RemoveSessionState | null>(null);

  const handleRemoveClick = useCallback((session: RemoveSessionState) => {
    setRemoveState(session);
  }, []);

  const handleRemoveCancel = useCallback(() => {
    setRemoveState(null);
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    if (!removeState) return;

    try {
      await client.mutate<
        RemovePlannedProcedureActivitySessionMutation,
        RemovePlannedProcedureActivitySessionMutationVariables
      >({
        mutation: RemovePlannedProcedureActivitySessionDocument,
        variables: {
          input: {
            procedureId: removeState.procedureId,
            sessionId: removeState.sessionId,
          },
        },
      });

      notify("Procedure.session.removeSuccess", { type: "success" });
      setRemoveState(null);
      refresh();
    } catch (error) {
      console.error("Failed to remove planned procedure session", {
        error,
        procedureId: removeState.procedureId,
        sessionId: removeState.sessionId,
      });
      notify("Procedure.session.removeError", { type: "error" });
    }
  }, [notify, refresh, removeState]);

  return (
    <Show title={<ProcedureTitle />}>
      <SimpleShowLayout>
        <FunctionField
          render={(record: ProcedureRecord) => (
            <ProcedureShowFields record={record} onRemoveClick={handleRemoveClick} />
          )}
        />
      </SimpleShowLayout>
      <CustomConfirm
        isOpen={removeState !== null}
        title={translate("Procedure.session.removeTitle")}
        content={translate("Procedure.session.removeContent", { title: removeState?.title ?? "" })}
        onConfirm={handleRemoveConfirm}
        onClose={handleRemoveCancel}
      />
    </Show>
  );
};
